import React, { useEffect, useRef, useState } from "react";
import { addNewEntry } from "../../actions/ApplicationReportAction";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Loading from "../Layout/Loading";
import EntryFormContent from "./EntryFormContent";
import { PRODUCTNAME } from "../../constants/fetchData";
import { getUserDetailsForApplicationSale } from "../../services/applicationReport";
import { getUserId, getDistributorNames } from "../../Common/functions";
const MAX_MOBILE_SCREEEN_SIZE = process.env.REACT_APP_MAX_MOBILE_SCREEN_SIZE;
const SUBMISSION_COOLDOWN = 15000;

const AddNewEntry = ({ userRole, closeModal: parentCloseModal }) => {
  const modalRef = useRef(null);
  const [isOpen, setIsOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [lastSubmitted, setLastSubmitted] = useState(0);
  const [userProfileDetails, setUserProfileDetails] = useState({});

  const [productGroupList, setProductGroupList] = useState([]);
  const [productNameOptions, setProductNameOptions] = useState([]);
  const [doctorsNameList, setDoctorsNameList] = useState([]);

  const [formData, setFormData] = useState({
    product_id: "",
    product_group: "",
    doctorsname: "",
    quantity: "",
    tracking_status: "Lead",
    patientname: "",
    patientemail: "",
    patientnumber: "",
    dates: "",
    distributor_name: "",
  });
  const [loading, setLoading] = useState(false);

  // Check if the screen is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < MAX_MOBILE_SCREEEN_SIZE); //FIXME:
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  const getUserProfileDetails = async () => {
    let result = [];
    result = await getUserDetailsForApplicationSale(
      // {"Distributor_Name":formData.Distributor_Name?formData.Distributor_Name :distributorsList[0]}
      {
        category:
          userRole === "super_admin" || userRole === "application_specialist"
            ? "user-profile"
            : userRole === "distributor_admin"
            ? "distributor-admin-profile"
            : "Internal-user-profile",
        UserId: getUserId(),
      }
    );
    console.log("re", result);
    setUserProfileDetails(result?.data[0]);
  };

  useEffect(() => {
    getUserProfileDetails();
    
    // Set default distributor if available
    const distributors = getDistributorNames();
    if (distributors.length > 0 && !formData.distributor_name) {
      setFormData(prev => ({
        ...prev,
        distributor_name: distributors[0]
      }));
    }
  }, []);

  const closeModal = () => {
    setIsOpen(false);
    setTimeout(() => {
      parentCloseModal();
    }, 400);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    console.log(`Field changed: ${name} = ${value}`); // Debug log

    if (name === "product_group") {
      const selectedGroup = productGroupList.find(
        (group) => group.group_name === value
      );
      setFormData({
        ...formData,
        product_group: value,
        product_name: selectedGroup ? selectedGroup.group_id : "",
        product_id: "",
        product_code: "",
        unit_price: "",
      });
    } else if (name === "product_id") {
      const selectedProduct = productNameOptions.find(
        (item) => item.item_id === value
      );
      setFormData({
        ...formData,
        [name]: value,
        product_code: selectedProduct ? selectedProduct.name : "",
        unit_price: selectedProduct ? selectedProduct.rate : "",
      });
    } else if (name === "distributor_name") {
      console.log(`Distributor changing in parent: ${value}`);
      setFormData({
        ...formData,
        distributor_name: value,
        doctorsname: "", // Clear doctor selection when distributor changes
      });
    } else if (name === "doctorsname") {
      const selectedDoctor = doctorsNameList.find(
        (doctor) => doctor.Full_Name === value
      );
      setFormData({
        ...formData,
        [name]: value,
        doctors_name: selectedDoctor ? selectedDoctor.id : "",
      });
    } else {
      console.log(`Updating formData: ${name} = ${value}`);
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  // Comprehensive form validation
  const validateForm = () => {
    const {
      product_group,
      quantity,
      doctorsname,
      tracking_status,
      product_id,
      patientname,
      dates,
    } = formData;

    // Required fields validation
    const requiredFieldsEmpty = [];

    if (!product_group) requiredFieldsEmpty.push("Product group");
    if (!quantity) requiredFieldsEmpty.push("Quantity");
    if (!doctorsname) requiredFieldsEmpty.push("Doctors name");
    if (!patientname) requiredFieldsEmpty.push("Patient name");
    if (!dates) requiredFieldsEmpty.push("Date");

    // Product name validation (required for Sale/NoSale/Demo tracking_status)
    if (tracking_status !== "Lead" && !product_id) {
      requiredFieldsEmpty.push("Product name");
    }

    if (requiredFieldsEmpty.length > 0) {
      const errorMessage =
        requiredFieldsEmpty.join(", ") +
        (requiredFieldsEmpty.length === 1 ? " is empty" : " are empty");
      toast.error(errorMessage);
      return false;
    }

    // Product code validation for Sale/NoSale tracking_status
    if (
      (tracking_status === "Sale" || tracking_status === "NoSale") &&
      (!formData.product_id || formData.product_id === "")
    ) {
      toast.error("Select product name");
      return false;
    }

    return true;
  };

  // Generate timestamp for unique identification
  // const generateTimestamp = () => {
  //   return new Date()
  //     .toISOString()
  //     .replace(/[-:]/g, "")
  //     .replace("T", "")
  //     .split(".")[0];
  // };
  const generateTimestamp = () => {
    const date = new Date();

    const pad = (n) => n.toString().padStart(2, "0");

    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1); // Months are 0-based
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // Prepare data for submission
  const prepareSubmissionData = () => {
    const timestampUnique = generateTimestamp();

    const data = {
      ...formData,
      // Add required fields based on jQuery logic
      UserId: getUserId(), // You'll need to get this from your app context
      username: userProfileDetails.username || "", // You'll need to get this from your app context
      timestamp: "",
      category: "sales tracking",
      group_title: userProfileDetails.GroupName || "", // You'll need to get this from your app context
      team: userProfileDetails.team || "", // You'll need to get this from your app context
      assigner: userProfileDetails.assignername || "", // You'll need to get this from your app context
      nickname: userProfileDetails.nickname, // You'll need to get this from your app context
      managerID: "manager_id_here", // You'll need to get this from your app context
      sales_order_status: "Pending",
      Name: timestampUnique,
      // doctors_name: formData.doctorsname,
      // distributorName: formData.distributor_name,
    };

    // Handle product code based on tracking_status
    if (formData.tracking_status === "Lead") {
      data.product_code = "";
      data.product_id = "";
      data.unit_price = "";
    } else if (formData.tracking_status === "Demo") {
      data.unit_price = "";
    } else {
      // For Sale, NoSale, Demo - you'll need to get these from your product selection
      data.product_code = formData.product_code;
      data.product_id = formData.product_id; // Adjust based on your data structure
    }

    return data;
  };

  // Create record in CRM
  const createCRMRecord = async (data) => {
    const crmFormat = JSON.stringify({ data: [data] });

    try {
      const response = await fetch(
        "https://46zd64zt92.execute-api.us-east-2.amazonaws.com/Prod/Create_records_custom_module_zoho_crm",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: crmFormat,
        }
      );
      const result = await response.json();
      // const responseBody = JSON.parse(result.body);
      // const recordId = JSON.parse(responseBody.text).data[0].details.id;
      const parsedText = JSON.parse(result.text); // parse the JSON string
      const recordId = parsedText.data[0].details.id;

      return recordId;
    } catch (error) {
      console.error("Error creating CRM record:", error);
      throw error;
    }
  };

  // Create sales record in CRM (for Sale tracking_status)
  const createSalesRecord = async (data) => {
    const crmFormat = JSON.stringify({ data: [data] });

    try {
      const response = await fetch(
        "https://46zd64zt92.execute-api.us-east-2.amazonaws.com/Prod/crm/Create_records_sales_module_zoho_crm",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: crmFormat,
        }
      );

      const result = await response.json();
      // const responseBody = JSON.parse(result.body);
      // const salesRecordId = JSON.parse(responseBody.text).data[0].details.id;
      const parsedText = JSON.parse(result.text); // parse the JSON string
      const salesRecordId = parsedText.data[0].details.id;

      return salesRecordId;
    } catch (error) {
      console.error("Error creating sales record:", error);
      throw error;
    }
  };

  // Submit to sales tracking
  const submitToSalesTracking = async (data) => {
    try {
      const response = await fetch(
        "https://46zd64zt92.execute-api.us-east-2.amazonaws.com/Prod/sales_tracking_distributorsales_post",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );

      return await response.json();
    } catch (error) {
      console.error("Error submitting to sales tracking:", error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    // Check submission cooldown
    const currentTime = new Date().getTime();
    if (currentTime - lastSubmitted < SUBMISSION_COOLDOWN) {
      toast.warning("Please wait before submitting again");
      return;
    }

    // Validate form
    if (!validateForm()) {
      return;
    }

    const submissionData = prepareSubmissionData();
    console.log("Submission data:", submissionData);
    setLoading(true);

    try {
      // Update last submission time
      setLastSubmitted(currentTime);

      // Prepare data
      const submissionData = prepareSubmissionData();
      console.log("Submission data:", submissionData);

      let recordId;
      let salesRecordId;

      // Create CRM record
      recordId = await createCRMRecord(submissionData);
      console.log("CRM Record ID:", recordId);

      // Add record ID to submission data
      submissionData.record_id = recordId;

      // Handle Sale tracking_status - create additional sales record
      if (submissionData.tracking_status === "Sale") {
        salesRecordId = await createSalesRecord(submissionData);
        console.log("Sales Record ID:", salesRecordId);
        submissionData.sales_record_id = salesRecordId;
      }

      // Submit to sales tracking
      const salesTrackingResponse = await submitToSalesTracking(submissionData);
      console.log("Sales tracking response:", salesTrackingResponse);

      toast.success("Successfully added");

      // Close modal and potentially redirect
      setTimeout(() => {
        closeModal();
        // If you need to redirect or refresh data, do it here
        // window.location.href = 'Sales-tracking-report(distributor).html';
      }, 1000);
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Error occurred during submission");
    } finally {
      setLoading(false);
    }
  };

  // const handleSubmit = async () => {
  //   console.log("formData", formData);
  //   if (!formData.product_id || !formData.patientname || !formData.dates) {
  //     toast.error("Please fill all required fields.");
  //     return;
  //   }

  //   setLoading(true);
  //   try {
  //     const response = await addNewEntry(formData);
  //     if (response.success) {
  //       toast.success("Successfully added");
  //       closeModal();
  //     } else {
  //       toast.error("Error occurred");
  //     }
  //   } catch (error) {
  //     toast.error("Something went wrong");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        closeModal();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <AnimatePresence>
        {isOpen &&
          (isMobile ? (
            // Mobile dialog (centered, no animation)
            <div
              ref={modalRef}
              className="bg-white w-[90%] max-w-md rounded-lg max-h-[70vh] overflow-y-auto px-6 shadow-lg"
            >
              {loading ? (
                <Loading />
              ) : (
                <>
                  <EntryFormContent
                    formData={formData}
                    handleChange={handleChange}
                    handleSubmit={handleSubmit}
                    handleCancel={parentCloseModal}
                    heading="Add New"
                    productGroupList={productGroupList}
                    setProductGroupList={setProductGroupList}
                    productNameOptions={productNameOptions}
                    setProductNameOptions={setProductNameOptions}
                    doctorsNameList={doctorsNameList}
                    setDoctorsNameList={setDoctorsNameList}
                  />
                </>
              )}
            </div>
          ) : (
            // Desktop slide-in panel
            <motion.div
              ref={modalRef}
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.4 }}
              className="bg-white lg:w-1/2 w-[80vw] h-full px-6 absolute right-0 top-0 shadow-lg"
            >
              {loading ? (
                <Loading />
              ) : (
                <>
                  <button
                    onClick={closeModal}
                    className="absolute top-0 -left-12 bg-gray-200 rounded-md p-1 shadow-md"
                  >
                    <X className="w-7 h-7 text-gray-500 hover:text-gray-800" />
                  </button>
                  <div className="pt-4">
                    <EntryFormContent
                      formData={formData}
                      handleChange={handleChange}
                      handleSubmit={handleSubmit}
                      heading="Add New"
                      productGroupList={productGroupList}
                      setProductGroupList={setProductGroupList}
                      productNameOptions={productNameOptions}
                      setProductNameOptions={setProductNameOptions}
                      doctorsNameList={doctorsNameList}
                      setDoctorsNameList={setDoctorsNameList}
                    />
                  </div>
                </>
              )}
            </motion.div>
          ))}
      </AnimatePresence>
    </div>
  );
};

export default AddNewEntry;
